import type { Asset } from '@compass/client/types/asset';
import { AssetType, AssetUploadStatus } from '@compass/client/types/asset';
import type { CallInfo } from '@compass/client/types/call';
import { CallStatus } from '@compass/client/types/call';
import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import type { Workflow } from '@compass/client/types/workflow';
import { AudioPlayer, type AudioPlayerHandle } from '@components/AudioPlayer/AudioPlayer';
import { AudioErrorBoundary, TranscriptErrorBoundary } from '@components/ErrorBoundary';
import { StatusDropdown } from '@components/StatusDropdown';
import { Transcript, TranscriptEmptyState } from '@components/Transcript/Transcript';
import { Button } from '@components/ui/button';
import { SLATimer } from '@components/ui/sla-timer';
import { useCall, useCaseRecordCalls } from '@hooks/useCalls';
import { useCaseRecordAssets } from '@hooks/useCaseRecordAssets';
import { useCase, useExecuteAction } from '@hooks/useCases';
import { useDownloadDocument } from '@hooks/useDownloadDocument';
import { usePayorEnhancedGroups } from '@hooks/usePayorEnhancedGroups';
import { useSchema } from '@hooks/useSchema';
import { useCurrentUser } from '@hooks/useUsers';
import { useWatchCaseRecord } from '@hooks/useWatchCaseRecord';
import { useStore } from '@store/index';
import { useQueryClient } from '@tanstack/react-query';
import { canPerformAction, executeCaseAction, handleCancelCaseAction } from '@utils/caseActions';
import { formatCaseId } from '@utils/cases';
import { getAssetFileExtension } from '@utils/download';
import { buildRoutePath, removeOrgPrefix, removeProgPrefix } from '@utils/routes';
import { slugifyString } from '@utils/string';
import { Copy, History, MoreVertical, PhoneForwarded, XCircle } from 'lucide-react';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { CaseInputForm, type CaseInputFormHandle } from '@/components/CaseInputForm';
import { CaseSchemaVersionProvider } from '@/components/fields/context';
import { Breadcrumb, BreadcrumbPortal, HeaderActionsPortal } from '@/components/Header';
import {
  KnipperCaseSummaryView,
  type KnipperCaseSummaryViewHandle,
} from '@/components/KnipperCaseSummaryView';
import { MultipleRecordingsBanner } from '@/components/MultipleRecordingsBanner';
import { RecordingSelectionModal } from '@/components/RecordingSelectionModal';
import { StatusConfirmationDialog } from '@/components/StatusConfirmationDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { KnipperCaseState } from '@/constants/caseStates';
import { isWarmTransferVariant, PQCWorkflowVariant, WorkflowType } from '@/constants/dispositions';
import { queryKeys } from '@/services/query.config';
import type { ActionDataParams } from '@/types/actions';
import type { FieldGroup } from '@/types/formSchema';
import type { StatusUpdatePlan } from '@/utils/bulkStatusUpdate';
import { caseSchemaVersion } from '@/utils/caseSchemaVersion';

import type { DefaultCaseDetailsPageProps } from './DefaultCaseDetailsPage';
import { KnipperCaseDetailProcessingPage } from './KnipperCaseDetailProcessingPage';

/**
 * Whether a call_document asset is finished generating and can be previewed /
 * downloaded — it has finished uploading (COMPLETED/UPLOADED, not PENDING/
 * UPLOADING/FAILED) and carries a content_type. Used both to decide when asset
 * polling can stop and to pick which asset to surface.
 */
const isDocumentUsable = (asset: Asset): boolean =>
  !!asset.content_type &&
  (asset.status === AssetUploadStatus.ASSET_UPLOAD_STATUS_COMPLETED ||
    asset.status === AssetUploadStatus.ASSET_UPLOAD_STATUS_UPLOADED);

/**
 * Whether a case is expected to get a call recording. Manually created cases
 * and cases created by an external integration through the API ('api') have
 * no recording, so the "Recording will be available shortly" copy is wrong
 * for them.
 */
const isRecordingBacked = (creationMethod: string | undefined): boolean =>
  creationMethod !== 'manual' && creationMethod !== 'api';

/**
 * Breadcrumb component for case details page
 */
interface CaseBreadcrumbProps {
  orgId: string;
  progId: string;
  caseId: string;
  code?: string;
}

const CaseBreadcrumb: React.FC<CaseBreadcrumbProps> = ({ orgId, progId, caseId, code }) => (
  <BreadcrumbPortal>
    <Breadcrumb>
      <Breadcrumb.Item
        to={buildRoutePath('/cases', removeOrgPrefix(orgId), removeProgPrefix(progId))}
      >
        Cases
      </Breadcrumb.Item>
      <Breadcrumb.Item>{formatCaseId(caseId, code)}</Breadcrumb.Item>
    </Breadcrumb>
  </BreadcrumbPortal>
);

/**
 * Header actions component for case details page
 */
interface CaseHeaderActionsProps {
  isLoading: boolean;
  caseDetails: GetCaseRecordResponse | undefined;
  selectedWorkflow: Workflow | null;
  onActionSelect: (action: string, caseId?: string, actionDataParams?: ActionDataParams) => void;
  onCancelCase: () => void;
  onMarkAsDuplicate: () => void;
  executeActionPending: boolean;
  orgId: string;
  progId: string;
}

const CaseHeaderActionsComponent: React.FC<CaseHeaderActionsProps> = ({
  isLoading,
  caseDetails,
  selectedWorkflow,
  onActionSelect,
  onCancelCase,
  onMarkAsDuplicate,
  executeActionPending,
}) => {
  const canCancelCase = canPerformAction('cancel_case', caseDetails?.state);
  const canMarkAsDuplicate = canPerformAction('mark_as_duplicate', caseDetails?.state);
  const hasMenuItems = canCancelCase || canMarkAsDuplicate;

  return (
    <HeaderActionsPortal>
      {!isLoading && caseDetails && (
        <>
          {/* Warm Transfer — record only indicator (AQ-357). Derived from the workflow
              variant; the case is a record-only entry with no manufacturer submission. */}
          {isWarmTransferVariant(selectedWorkflow?.variant) && (
            <span
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-muted-foreground"
              title="Warm Transfer — record only"
            >
              <PhoneForwarded className="h-3.5 w-3.5" />
              Warm Transfer — record only
            </span>
          )}
          {/* SLA Timer — AQ-165. Shows remaining time + breach signals for running
              and paused cases; renders nothing for submitted / acked / completed /
              cancelled / other terminal states (those are conveyed by the status
              badge and state-specific sections elsewhere on the page).
              The data-testid gives Playwright a stable anchor independent of
              Tailwind utility class churn. */}
          <span data-testid="case-details-sla-timer" className="inline-flex">
            <SLATimer
              workflowLastStartedAt={caseDetails.workflow_last_started_at}
              workflowDurationSeconds={caseDetails.workflow_duration_seconds}
              slaMinutes={selectedWorkflow?.sla?.minutes}
              isBreached={caseDetails.status_flags?.sla_breach}
              completedAt={caseDetails.workflow_completed_at || caseDetails.workflow_cancelled_at}
              workflowState={caseDetails.workflow_state}
              workflowType={selectedWorkflow?.workflow_type}
              caseState={caseDetails.state}
              slaRiskEscalated={caseDetails.status_flags?.sla_risk_escalated}
              workflowVersion={selectedWorkflow?.version}
              variant="details"
            />
          </span>
          {/* Status Dropdown */}
          <StatusDropdown
            item={caseDetails}
            selectedWorkflow={selectedWorkflow}
            onActionSelect={onActionSelect}
          />

          {/* More Options Dropdown - only render if there are items to show */}
          {hasMenuItems && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" title="More options">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {/* Cancel Case option */}
                {canCancelCase && (
                  <DropdownMenuItem
                    onClick={onCancelCase}
                    className="cursor-pointer"
                    disabled={executeActionPending}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Cancel Case
                  </DropdownMenuItem>
                )}
                {/* Mark as Duplicate option */}
                {canMarkAsDuplicate && (
                  <DropdownMenuItem
                    onClick={onMarkAsDuplicate}
                    className="cursor-pointer"
                    disabled={executeActionPending}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Mark as Duplicate
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </>
      )}
    </HeaderActionsPortal>
  );
};

const CaseHeaderActions = React.memo(CaseHeaderActionsComponent);
CaseHeaderActions.displayName = 'CaseHeaderActions';

/**
 * Case content component - renders different views based on state
 */
interface CaseContentProps {
  viewMode: KnipperCaseState | null;
  currentState: string;
  caseId: string;
  caseDetails: GetCaseRecordResponse | undefined;
  inputGroups: FieldGroup[];
  outputGroups: FieldGroup[];
  isEditingFromReview: boolean;
  initialFormStep: number;
  handleSaveAndGoToReview: () => void;
  transcriptAssetsLoading: boolean;
  selectedCallId: string | null;
  callDetails: CallInfo | null | undefined;
  audioCurrentTime: number;
  handleSegmentClick: (time: number) => void;
  selectedCallStatus: CallStatus | undefined;
  transcriptAssets: Asset[];
  hasAudioAsset: boolean;
  audioPlayerRef: React.RefObject<AudioPlayerHandle>;
  audioAssets: Asset[];
  setAudioCurrentTime: (time: number) => void;
  documentAssetId: string | null;
  onDownloadDocument: (assetId: string) => void;
  caseInputFormRef: React.RefObject<CaseInputFormHandle>;
  caseSummaryViewRef: React.RefObject<KnipperCaseSummaryViewHandle>;
  onSelectCall?: (callId: string, onSuccess?: () => void) => void;
  // Banner props
  hasMultipleRecordings: boolean;
  recordingCount: number;
  onViewRecordings: () => void;
  // AI suggestions
  extractedInputData?: Record<string, any> | null;
  extractedInputHints?: Record<string, string> | null;
  // Portal variant
  isPortalVariant: boolean;
}

const CaseContent: React.FC<CaseContentProps> = ({
  viewMode,
  currentState,
  caseId,
  caseDetails,
  inputGroups,
  outputGroups,
  isEditingFromReview,
  initialFormStep,
  handleSaveAndGoToReview,
  transcriptAssetsLoading,
  selectedCallId,
  callDetails,
  audioCurrentTime,
  handleSegmentClick,
  selectedCallStatus,
  transcriptAssets,
  hasAudioAsset,
  audioPlayerRef,
  audioAssets,
  setAudioCurrentTime,
  documentAssetId,
  onDownloadDocument,
  caseInputFormRef,
  caseSummaryViewRef,
  onSelectCall,
  hasMultipleRecordings,
  recordingCount,
  onViewRecordings,
  extractedInputData,
  extractedInputHints,
  isPortalVariant,
}) => {
  // Use viewMode if set, otherwise fall back to currentState
  const effectiveView = viewMode || currentState;

  switch (effectiveView) {
    case KnipperCaseState.START:
      return <KnipperCaseDetailProcessingPage title="Creating case" />;
    case KnipperCaseState.INTAKE_PROCESSING:
    case KnipperCaseState.CREATED:
    case KnipperCaseState.AWAITING_REVIEW:
      return <KnipperCaseDetailProcessingPage />;

    case KnipperCaseState.INTAKE_DRAFT:
      return (
        <div className="flex flex-col h-full overflow-hidden">
          {/* Two-column layout: Transcript + CaseInputForm */}
          <div className="flex flex-1 min-h-0">
            {/* Left: Transcript */}
            <div className="w-1/3 flex flex-col min-h-0 overflow-hidden">
              {/* Multiple Recordings Banner */}
              {hasMultipleRecordings && (
                <MultipleRecordingsBanner
                  recordingCount={recordingCount}
                  onViewRecordings={onViewRecordings}
                />
              )}
              <div className="px-4 pt-4 pb-2 shrink-0">
                <h3 className="text-muted-foreground font-medium text-sm">Transcript</h3>
              </div>
              <div className="flex-1 min-h-0 flex flex-col">
                {selectedCallId ? (
                  <TranscriptErrorBoundary>
                    <Transcript
                      className="h-full"
                      caseRecordId={caseId}
                      callId={selectedCallId}
                      callSid={callDetails?.sid}
                      currentTime={audioCurrentTime}
                      onSegmentClick={handleSegmentClick}
                      callStatus={selectedCallStatus}
                      assets={transcriptAssets}
                      isLoadingAssets={transcriptAssetsLoading}
                      enableSearch
                    />
                  </TranscriptErrorBoundary>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <TranscriptEmptyState
                      message={
                        isRecordingBacked(caseDetails?.creation_method)
                          ? 'Recording will be available shortly'
                          : 'No transcript available'
                      }
                      description={
                        isRecordingBacked(caseDetails?.creation_method)
                          ? 'Transcripts and audio generation in progress'
                          : caseDetails?.creation_method === 'api'
                            ? 'This case was created via API without a call recording'
                            : 'This case was created manually without a call recording'
                      }
                      icon={
                        isRecordingBacked(caseDetails?.creation_method) ? (
                          <History className="w-12 h-12 mb-3 text-muted-foreground" />
                        ) : undefined
                      }
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Right: Case Input Form with Stepper */}
            <div className="w-2/3 flex flex-col min-h-0 border-l overflow-hidden">
              <CaseInputForm
                ref={caseInputFormRef}
                inputGroups={inputGroups}
                caseDetails={caseDetails}
                caseId={caseId}
                callId={selectedCallId}
                initialStep={isEditingFromReview ? initialFormStep : 0}
                extractedInputData={extractedInputData}
                extractedInputHints={extractedInputHints}
                lastStepNext={
                  isEditingFromReview
                    ? {
                        label: 'Save and Go To Review',
                        onNext: handleSaveAndGoToReview,
                      }
                    : undefined
                }
              />
            </div>
          </div>

          {/* Bottom: Audio Player audio player */}
          {hasAudioAsset && (
            <div className="border-t shrink-0">
              <AudioErrorBoundary>
                <AudioPlayer
                  ref={audioPlayerRef}
                  caseRecordId={caseId}
                  caseCode={caseDetails?.code}
                  callId={selectedCallId!}
                  callDetails={callDetails}
                  onTimeUpdate={setAudioCurrentTime}
                  assets={audioAssets}
                />
              </AudioErrorBoundary>
            </div>
          )}
        </div>
      );

    case KnipperCaseState.REVIEW_NEEDED:
      return (
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex-1 overflow-hidden">
            <KnipperCaseSummaryView
              ref={caseSummaryViewRef}
              caseId={caseId}
              caseDetails={caseDetails}
              fieldGroups={inputGroups}
              outputGroups={outputGroups}
              caseState={currentState as KnipperCaseState}
              documentAssetId={documentAssetId}
              onDownloadDocument={onDownloadDocument}
              onSelectCall={onSelectCall}
              hasMultipleRecordings={hasMultipleRecordings}
              recordingCount={recordingCount}
              onViewRecordings={onViewRecordings}
              isPortalVariant={isPortalVariant}
            />
          </div>
        </div>
      );

    case KnipperCaseState.DELIVERING_EMAIL:
    case KnipperCaseState.SUBMITTED:
    case KnipperCaseState.ACKNOWLEDGED:
    case KnipperCaseState.COMPLETED:
    case KnipperCaseState.NON_REPORTABLE:
    case KnipperCaseState.DUPLICATE:
    case KnipperCaseState.CANCELLED: {
      // Cancelled cases (non-reportable, duplicate) should not show the download button
      // since the form was not submitted
      const isCancelled =
        currentState === KnipperCaseState.NON_REPORTABLE ||
        currentState === KnipperCaseState.DUPLICATE;

      return (
        <KnipperCaseSummaryView
          ref={caseSummaryViewRef}
          caseId={caseId}
          caseDetails={caseDetails}
          fieldGroups={inputGroups}
          outputGroups={outputGroups}
          caseState={currentState as KnipperCaseState}
          documentAssetId={isCancelled ? null : documentAssetId}
          onDownloadDocument={isCancelled ? undefined : onDownloadDocument}
          isPortalVariant={isPortalVariant}
        />
      );
    }

    default:
      return (
        <div className="p-8">
          <h2 className="text-xl font-semibold mb-4">Unknown State</h2>
          <p className="text-muted-foreground">Current state: {currentState}</p>
          <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded">
            <p className="text-sm">This state doesn&apos;t have a specific view yet.</p>
          </div>
        </div>
      );
  }
};

/**
 * Knipper Case Details Page for PQC Workflow
 * Shows different components based on case state
 */
export const KnipperCaseDetailsPage: React.FC<DefaultCaseDetailsPageProps> = ({
  caseId,
  orgId,
  progId,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch case details to get the current state
  // refetchOnMount: 'always' ensures fresh data when navigating back to this page,
  // even if cache was recently updated (e.g., invalidation refetch got stale backend data)
  const { data: caseDetails, isLoading } = useCase(caseId, {
    refetchInterval: 10_000,
    refetchOnMount: 'always',
  });

  // Watch for real-time case record updates via SSE — pushes input_data changes
  // (e.g. portal session URL) immediately instead of waiting for the next poll
  useWatchCaseRecord(caseId);

  // Current user profile — the manufacturer form requires the submitter's name
  // and title (the PQC agent rejects the submission without them), so they are
  // checked before submit actions rather than after the round-trip
  const { data: currentUserResponse } = useCurrentUser();

  // Get workflow from store
  const { getSelectedWorkflow, selectedCallId, setSelectedCallId, resetSelectedCallId } =
    useStore();
  const selectedWorkflow = getSelectedWorkflow();

  // Fetch schema from schema service (pass workflow_type so PQC uses local JSON schema)
  const { inputGroups: schemaInputGroups, outputGroups } = useSchema(
    selectedWorkflow?.id,
    caseSchemaVersion(caseDetails),
  );

  // Replace primary_payer_name static options with live payor data for unifiedmedbv
  const { inputGroups } = usePayorEnhancedGroups(
    schemaInputGroups,
    selectedWorkflow?.workflow_type,
  );

  // Reset selectedCallId when caseId changes to ensure correct call is selected for each case
  useEffect(() => {
    resetSelectedCallId();
  }, [caseId, resetSelectedCallId]);

  // Action execution
  const executeAction = useExecuteAction();

  // Audio player ref and state
  const audioPlayerRef = useRef<AudioPlayerHandle | null>(null);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);

  // Case input form ref for validation
  const caseInputFormRef = useRef<CaseInputFormHandle | null>(null);

  // Case summary view ref for validation (when in review mode)
  const caseSummaryViewRef = useRef<KnipperCaseSummaryViewHandle | null>(null);

  // View mode state - controls which view to show (can differ from case state)
  // Uses KnipperCaseState values to determine view:
  // - INTAKE_DRAFT/CREATED: shows transcript + CaseInputForm
  // - REVIEW_NEEDED: shows KnipperCaseSummaryView
  const [viewMode, setViewMode] = useState<KnipperCaseState | null>(null);

  // Track when user manually enters edit mode from review state
  // This prevents backend refetch from overriding user's active edit session
  const [isUserEditMode, setIsUserEditMode] = useState(false);

  // Initial step for CaseInputForm when navigating from review view
  const [initialFormStep] = useState<number>(0);

  // Portal variant detection
  const isPortalVariant = selectedWorkflow?.variant === PQCWorkflowVariant.PORTAL;

  // Confirmation dialog state
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    statusUpdatePlans: StatusUpdatePlan[];
  }>({
    isOpen: false,
    statusUpdatePlans: [],
  });

  // Multiple recordings modal state
  const [isRecordingsModalOpen, setIsRecordingsModalOpen] = useState(false);

  // Initialize viewMode based on case state when data loads
  // Only preserve viewMode when user is actively editing (isUserEditMode=true)
  useEffect(() => {
    if (caseDetails?.state) {
      const newState = caseDetails.state as KnipperCaseState;

      // Only preserve edit mode if user manually clicked Edit from review state
      if (isUserEditMode && newState === KnipperCaseState.REVIEW_NEEDED) {
        return; // Don't override user's active edit session
      }

      // Sync viewMode to backend state
      setViewMode(newState);
    }
  }, [caseDetails?.state, isUserEditMode]);

  // Clean up audio when component unmounts
  useEffect(() => {
    return () => {
      audioPlayerRef.current?.stop();
      setAudioCurrentTime(0);
    };
  }, []);

  // Fetch the list of calls for this case record
  const { data: callsData } = useCaseRecordCalls(caseId, {
    refetchInterval: 30000,
  });

  // Auto-select call when calls load or contact_id changes
  // For PQC workflow: select the call matching the case's contact_id (updates when contact_id changes)
  // For other workflows: select the first call (only on initial load)
  useEffect(() => {
    if (!callsData?.calls || callsData.calls.length === 0) return;

    const isPQC = selectedWorkflow?.workflow_type === WorkflowType.PQC;
    const caseContactId = caseDetails?.input_data?.data?.contact_id as string | undefined;

    // For PQC workflow, always sync selected call with the case's contact_id
    if (isPQC && caseContactId) {
      const matchingCall = callsData.calls.find(
        call => call.input_data?.contact_id === caseContactId,
      );
      if (matchingCall?.id && matchingCall.id !== selectedCallId) {
        setSelectedCallId(matchingCall.id);
        return;
      }
    }

    // For non-PQC workflows or if no contact_id match, select first call only if none selected
    if (!selectedCallId) {
      const firstCallId = callsData.calls[0].id;
      if (firstCallId) {
        setSelectedCallId(firstCallId);
      }
    }
  }, [
    callsData?.calls,
    selectedCallId,
    setSelectedCallId,
    selectedWorkflow?.workflow_type,
    caseDetails?.input_data?.data?.contact_id,
  ]);

  // Fetch the selected call details
  const { data: selectedCallData } = useCall(caseId, selectedCallId || null, {
    refetchInterval: 10000,
  });
  const callDetails = selectedCallData?.call;

  // Extract AI suggestions and hints from call details
  const extractedInputData = callDetails?.extracted_data?.input?.data || null;
  const extractedInputHints = callDetails?.extracted_data?.input?.metadata || null;

  // Get the status of the selected call
  const selectedCallStatus = useMemo(() => {
    if (!selectedCallId || !callsData?.calls) return undefined;
    const selectedCall = callsData.calls.find(call => call.id === selectedCallId);
    return selectedCall?.status;
  }, [selectedCallId, callsData?.calls]);

  // Check if there are multiple recordings for the banner
  const hasMultipleRecordings = (callsData?.calls?.length || 0) > 1;

  // Check if user can attach recordings (only in intake_draft or review_needed states)
  const canAttachRecording =
    caseDetails?.state === KnipperCaseState.INTAKE_DRAFT ||
    caseDetails?.state === KnipperCaseState.REVIEW_NEEDED;

  // Get the currently attached call ID from case's contact_id
  const currentlyAttachedCallId = useMemo(() => {
    const caseContactId = caseDetails?.input_data?.data?.contact_id as string | undefined;
    if (!caseContactId || !callsData?.calls) return undefined;
    const matchingCall = callsData.calls.find(
      call => call.input_data?.contact_id === caseContactId,
    );
    return matchingCall?.id;
  }, [caseDetails?.input_data?.data?.contact_id, callsData?.calls]);

  // Get the selected call's embedded assets from the calls list response
  // The listAssets API doesn't filter by call_id, so we use embedded IDs instead
  const selectedCallEmbeddedAssets = useMemo(() => {
    if (!selectedCallId || !callsData?.calls) return undefined;
    const call = callsData.calls.find((c: CallInfo) => c.id === selectedCallId);
    return call?.assets?.[0];
  }, [selectedCallId, callsData?.calls]);

  const audioAssets = useMemo((): Asset[] => {
    const recordingId = selectedCallEmbeddedAssets?.recording;
    if (!recordingId) return [];
    return [{ id: recordingId } as Asset];
  }, [selectedCallEmbeddedAssets?.recording]);

  const transcriptAssets = useMemo((): Asset[] => {
    const transcriptId = selectedCallEmbeddedAssets?.transcript;
    if (!transcriptId) return [];
    return [{ id: transcriptId } as Asset];
  }, [selectedCallEmbeddedAssets?.transcript]);
  const transcriptAssetsLoading = selectedCallId != null && !callsData?.calls;

  // Fetch document assets at the case record level (not tied to specific call).
  // Keep polling until the document is actually usable (see isDocumentUsable):
  // the call_document asset is generated on entry to review and can appear in
  // the list while still PENDING/UPLOADING (no content_type yet). Stopping then
  // would leave the preview on an unrenderable asset until a manual refresh.
  const { data: documentAssets = [] } = useCaseRecordAssets(
    caseId,
    AssetType.ASSET_TYPE_CALL_DOCUMENT,
    {
      pollingInterval: 10000,
      stopPollingWhenFound: true,
      isFound: assets => assets.some(isDocumentUsable),
    },
  );

  // Download document hook
  const { downloadDoc } = useDownloadDocument();

  // Check if we have audio assets
  const hasAudioAsset = audioAssets.length > 0;

  // Get the document asset (only one document per case). Prefer a usable
  // (finished-uploading, content-typed) asset; fall back to whatever exists so
  // download/other consumers still see the row.
  const documentAsset = useMemo(
    () => documentAssets.find(isDocumentUsable) ?? documentAssets[0],
    [documentAssets],
  );
  const documentAssetId = useMemo(() => {
    return documentAsset?.id || null;
  }, [documentAsset]);

  // Download document handler with proper filename format
  const handleDownloadDocument = useCallback(
    (assetId: string) => {
      const patientName = caseDetails?.input_data?.data?.patient_full_name as string | undefined;
      const slugifiedName = patientName ? slugifyString(patientName) : '';
      const timestamp = moment().format('YYYY-MM-DDTHH-mm-ss');
      const ext = getAssetFileExtension(documentAsset?.file_path, documentAsset?.content_type);
      const filename = slugifiedName
        ? `AE-${slugifiedName}-${timestamp}${ext}`
        : `AE-${timestamp}${ext}`;
      downloadDoc({ assetId, caseRecordId: caseId, filename });
    },
    [caseDetails?.input_data?.data?.patient_full_name, caseId, downloadDoc, documentAsset],
  );

  // Handle transcript segment click to seek audio
  const handleSegmentClick = useCallback((time: number) => {
    audioPlayerRef.current?.seekTo(time);
  }, []);

  // Handle action selection - show confirmation dialog
  const handleActionSelect = useCallback(
    (action: string, _caseIdParam?: string, actionDataParams?: ActionDataParams) => {
      if (!caseId) return;

      const statusUpdatePlan: StatusUpdatePlan = {
        action,
        caseIds: [caseId],
        count: 1,
        actionDataParams,
      };

      setConfirmationDialog({
        isOpen: true,
        statusUpdatePlans: [statusUpdatePlan],
      });
    },
    [caseId],
  );
  // Handle attach to case action - directly executes without confirmation dialog
  // The UI will update when the case record refetches with the new contact_id
  const handleSelectCall = useCallback(
    (callId: string, onModalClose?: () => void) => {
      if (!caseId) return;
      executeCaseAction(
        'attach_to_case',
        caseId,
        selectedWorkflow?.workflow_type,
        executeAction.mutate,
        {
          onSuccess: () => {
            setIsRecordingsModalOpen(false);
            onModalClose?.();
            // Invalidate calls so embedded asset IDs refresh immediately after attach
            queryClient.invalidateQueries({ queryKey: ['caseRecordCalls', caseId] });
          },
        },
        { call_id: callId },
      );
    },
    [caseId, selectedWorkflow?.workflow_type, executeAction.mutate, queryClient],
  );

  // Handle opening recordings modal - stop main player first
  const handleOpenRecordingsModal = useCallback(() => {
    audioPlayerRef.current?.stop();
    setIsRecordingsModalOpen(true);
  }, []);

  // Handle confirmation dialog response
  const handleConfirmAction = async (note?: string, scheduledAt?: string) => {
    if (!caseId || !caseDetails || confirmationDialog.statusUpdatePlans.length === 0) {
      return;
    }

    const plan = confirmationDialog.statusUpdatePlans[0];
    const action = plan.action;

    // Build action data params with reason/note for PQC actions
    const actionDataParams = {
      ...plan.actionDataParams,
      reason: note, // Pass as reason for cancel_workflow/mark_as_duplicate
      actionNote: note, // Also create a case note with the reason
      scheduled_at: scheduledAt,
    };

    // Actions that require field validation before execution
    const actionsRequiringValidation = ['submit_for_review', 'submit_to_manufacturer'];

    if (actionsRequiringValidation.includes(action)) {
      // The PQC agent requires the submitter's profile name and title on the
      // manufacturer form — surface a missing profile before submitting instead
      // of letting the case bounce back from validation. Only enforced when the
      // profile has loaded; otherwise the agent-side check still catches it.
      const profile = currentUserResponse?.user;
      if (profile) {
        const missingProfileFields: string[] = [];
        if (!profile.name?.trim()) missingProfileFields.push('User Name');
        if (!profile.title?.trim()) missingProfileFields.push('User Title');
        if (missingProfileFields.length > 0) {
          toast.error(
            `${missingProfileFields.join(' and ')} ${
              missingProfileFields.length > 1 ? 'are' : 'is'
            } missing — please set this in Settings before submitting.`,
          );
          setConfirmationDialog({ isOpen: false, statusUpdatePlans: [] });
          return;
        }
      }

      // Check which view is currently active
      const hasFormRef = caseInputFormRef.current !== null;
      const hasSummaryRef = caseSummaryViewRef.current !== null;

      // Validate using the active ref
      let isValid = true;
      if (hasFormRef) {
        isValid = caseInputFormRef.current?.validateAll() ?? true;
      } else if (hasSummaryRef) {
        isValid = caseSummaryViewRef.current?.validateAll() ?? true;
      }

      if (!isValid) {
        // Close the dialog - errors are shown in the form/summary view
        setConfirmationDialog({ isOpen: false, statusUpdatePlans: [] });
        return;
      }

      // Save unsaved form data before executing the action to ensure
      // the backend has the latest field values (e.g., product name & strength)
      let saved = true;
      if (hasFormRef) {
        saved = (await caseInputFormRef.current?.saveFormData()) ?? true;
      } else if (hasSummaryRef) {
        saved = (await caseSummaryViewRef.current?.saveFormData()) ?? true;
      }

      if (!saved) {
        // Save failed - don't proceed with the action
        setConfirmationDialog({ isOpen: false, statusUpdatePlans: [] });
        return;
      }
    }

    // Handle cancel case action specifically
    if (action === 'cancel_case') {
      handleCancelCaseAction(
        caseId,
        executeAction.mutate,
        {
          onCloseDialog: () => setConfirmationDialog({ isOpen: false, statusUpdatePlans: [] }),
          onCloseModal: () => {
            // Navigate back to cases list
            setTimeout(() => {
              queryClient.resetQueries({ queryKey: queryKeys.cases.byId(caseId || '') });
            }, 1_500);
          },
        },
        // buildCancelWorkflowActionData reads `cancelReason`, not `reason`, so
        // without this the operator's text never reached the workflow signal and
        // the cancellation fell back to the FSM's default reason. Mirrors
        // DefaultCaseDetailsPage's cancel_case branch.
        { ...actionDataParams, cancelReason: note },
      );
      return;
    }

    // Handle mark as duplicate action
    if (action === 'mark_as_duplicate') {
      executeCaseAction(
        action,
        caseId,
        selectedWorkflow?.workflow_type,
        executeAction.mutate,
        {
          onCloseDialog: () => setConfirmationDialog({ isOpen: false, statusUpdatePlans: [] }),
          onSuccess: () => {
            setTimeout(() => {
              queryClient.resetQueries({ queryKey: queryKeys.cases.byId(caseId || '') });
            }, 1_500);
          },
        },
        actionDataParams,
      );
      return;
    }

    // Handle submit for review action - navigate to calls
    if (action === 'submit_for_review') {
      executeCaseAction(action, caseId, selectedWorkflow?.workflow_type, executeAction.mutate, {
        onCloseDialog: () => setConfirmationDialog({ isOpen: false, statusUpdatePlans: [] }),
        onSuccess: () => {
          setTimeout(() => {
            queryClient.resetQueries({ queryKey: queryKeys.cases.byId(caseId || '') });
          }, 1_500);
        },
      });
      return;
    }

    // Execute the action with workflow type
    executeCaseAction(
      action,
      caseId,
      selectedWorkflow?.workflow_type,
      executeAction.mutate,
      {
        onCloseDialog: () => setConfirmationDialog({ isOpen: false, statusUpdatePlans: [] }),
        onCloseModal: () => {
          // Navigate back to cases list
          const casesPath = buildRoutePath('/cases', orgId || '', progId || '');
          navigate(casesPath);
        },
      },
      plan.actionDataParams,
      { caseState: caseDetails?.state },
    );
  };

  const handleCancelAction = () => {
    setConfirmationDialog({ isOpen: false, statusUpdatePlans: [] });
  };

  // Handler for "Save and Go To Review" button click in IntakeDraft view
  const handleSaveAndGoToReview = useCallback(() => {
    setViewMode(KnipperCaseState.REVIEW_NEEDED);
    setIsUserEditMode(false);
  }, []);

  // Handler for cancel case button click
  const handleCancelCase = useCallback(() => {
    if (!caseDetails) return;

    const statusUpdatePlan: StatusUpdatePlan = {
      action: 'cancel_case',
      caseIds: [caseDetails.id!],
      count: 1,
    };
    setConfirmationDialog({
      isOpen: true,
      statusUpdatePlans: [statusUpdatePlan],
    });
  }, [caseDetails]);

  // Handler for mark as duplicate button click
  const handleMarkAsDuplicate = useCallback(() => {
    if (!caseDetails) return;

    const statusUpdatePlan: StatusUpdatePlan = {
      action: 'mark_as_duplicate',
      caseIds: [caseDetails.id!],
      count: 1,
    };
    setConfirmationDialog({
      isOpen: true,
      statusUpdatePlans: [statusUpdatePlan],
    });
  }, [caseDetails]);

  // Get current state from case details
  const currentState = caseDetails?.state || 'unknown';

  // Check if we're editing from review state (viewMode is INTAKE_DRAFT but actual state is REVIEW_NEEDED)
  const isEditingFromReview =
    viewMode === KnipperCaseState.INTAKE_DRAFT && currentState === KnipperCaseState.REVIEW_NEEDED;

  // Loading state
  if (isLoading) {
    return (
      <>
        <CaseBreadcrumb orgId={orgId || ''} progId={progId || ''} caseId={caseId || ''} />
        <CaseHeaderActions
          isLoading={isLoading}
          caseDetails={caseDetails}
          selectedWorkflow={selectedWorkflow}
          onActionSelect={handleActionSelect}
          onCancelCase={handleCancelCase}
          onMarkAsDuplicate={handleMarkAsDuplicate}
          executeActionPending={executeAction.isPending}
          orgId={orgId || ''}
          progId={progId || ''}
        />
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
        </div>
      </>
    );
  }

  return (
    <CaseSchemaVersionProvider schemaVersion={caseSchemaVersion(caseDetails)}>
      {caseId && (
        <CaseBreadcrumb
          orgId={orgId || ''}
          progId={progId || ''}
          caseId={caseId || ''}
          code={caseDetails?.code}
        />
      )}
      <CaseHeaderActions
        isLoading={isLoading}
        caseDetails={caseDetails}
        selectedWorkflow={selectedWorkflow}
        onActionSelect={handleActionSelect}
        onCancelCase={handleCancelCase}
        onMarkAsDuplicate={handleMarkAsDuplicate}
        executeActionPending={executeAction.isPending}
        orgId={orgId || ''}
        progId={progId || ''}
      />
      {/* State-specific content */}
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-hidden">
          <CaseContent
            viewMode={viewMode}
            currentState={currentState}
            caseId={caseId || ''}
            caseDetails={caseDetails}
            inputGroups={inputGroups}
            outputGroups={outputGroups}
            isEditingFromReview={isEditingFromReview}
            initialFormStep={initialFormStep}
            handleSaveAndGoToReview={handleSaveAndGoToReview}
            transcriptAssetsLoading={transcriptAssetsLoading}
            selectedCallId={selectedCallId}
            callDetails={callDetails}
            audioCurrentTime={audioCurrentTime}
            handleSegmentClick={handleSegmentClick}
            selectedCallStatus={selectedCallStatus}
            transcriptAssets={transcriptAssets}
            hasAudioAsset={hasAudioAsset}
            audioPlayerRef={audioPlayerRef}
            audioAssets={audioAssets}
            setAudioCurrentTime={setAudioCurrentTime}
            documentAssetId={documentAssetId}
            onDownloadDocument={handleDownloadDocument}
            caseInputFormRef={caseInputFormRef}
            caseSummaryViewRef={caseSummaryViewRef}
            onSelectCall={handleSelectCall}
            hasMultipleRecordings={hasMultipleRecordings}
            recordingCount={callsData?.calls?.length || 0}
            onViewRecordings={handleOpenRecordingsModal}
            extractedInputData={extractedInputData}
            extractedInputHints={extractedInputHints}
            isPortalVariant={isPortalVariant}
          />
        </div>
      </div>

      {/* Confirmation Dialog */}
      <StatusConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmAction}
        statusUpdatePlans={confirmationDialog.statusUpdatePlans}
        workflow={selectedWorkflow}
        cases={caseDetails ? [caseDetails] : []}
        isLoading={executeAction.isPending}
        isIndividual={true}
      />

      {/* Recording Selection Modal */}
      <RecordingSelectionModal
        isOpen={isRecordingsModalOpen}
        onClose={() => setIsRecordingsModalOpen(false)}
        calls={callsData?.calls || []}
        caseId={caseId || ''}
        canAttach={canAttachRecording}
        onAttach={handleSelectCall}
        currentlyAttachedCallId={currentlyAttachedCallId}
      />
    </CaseSchemaVersionProvider>
  );
};
